// src/errors/index.ts
var ErrorCategory = {
  VALIDATION: "validation",
  AUTHENTICATION: "authentication",
  AUTHORIZATION: "authorization",
  NOT_FOUND: "not_found",
  CONFLICT: "conflict",
  RATE_LIMIT: "rate_limit",
  EXTERNAL_SERVICE: "external_service",
  DATABASE: "database",
  CONFIGURATION: "configuration",
  INTERNAL: "internal",
  DATA: "data",
  PIPELINE: "pipeline"
};
var DOCS_BASE = "https://docs.authe.io/errors";
function spec(code, description, userAction, category, defaultStatus, opts = {}) {
  return {
    code,
    description,
    userAction,
    documentationUrl: `${DOCS_BASE}/${code}`,
    category,
    defaultStatus,
    retryable: opts.retryable ?? false,
    severity: opts.severity ?? "ERROR"
  };
}
var ErrorCode = {
  // Validation
  VAL001: spec("VAL001", "Missing required field", "Provide all required fields and retry the request.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL002: spec("VAL002", "Invalid field format", "Check the field format documentation and correct the value.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL003: spec("VAL003", "Invalid field value", "Use one of the allowed values listed in the documentation.", ErrorCategory.VALIDATION, 400, { severity: "WARNING" }),
  VAL004: spec("VAL004", "Invalid response structure", "An internal response could not be parsed; retry, then contact support if persistent.", ErrorCategory.VALIDATION, 500),
  // Authentication
  AUTH001: spec("AUTH001", "Invalid credentials", "Sign in again with valid credentials.", ErrorCategory.AUTHENTICATION, 401),
  AUTH002: spec("AUTH002", "Token expired", "Refresh your session and retry.", ErrorCategory.AUTHENTICATION, 401),
  AUTH003: spec("AUTH003", "Invalid token", "Sign out, sign back in, and retry.", ErrorCategory.AUTHENTICATION, 401),
  // Authorization
  PERM001: spec("PERM001", "Insufficient permissions", "Ask an admin to grant the required permission, or use a different account.", ErrorCategory.AUTHORIZATION, 403),
  PERM002: spec("PERM002", "Resource access denied", "You do not have access to this resource within your tenant.", ErrorCategory.AUTHORIZATION, 403),
  // Packaging, not permission. PERM001's remedy ("ask an admin to grant the
  // required permission") is actively wrong for a plan refusal: nobody in the
  // customer's own org can grant an entitlement their subscription lacks.
  PERM003: spec("PERM003", "Not included in your plan", "This feature is not part of your current subscription. Contact sales to add it.", ErrorCategory.AUTHORIZATION, 403),
  // A cap, not an absence. PERM003 says the feature is not yours; this says it
  // is, and it is full — so the remedy is to free a slot or move tier, never to
  // retry (which is why this is not the 429 RATE001).
  PERM004: spec("PERM004", "Plan limit reached", "Remove an existing item to free a slot, or upgrade for a higher limit.", ErrorCategory.AUTHORIZATION, 403),
  // Resource
  RES001: spec("RES001", "Resource not found", "Verify the identifier; the resource may have been deleted or never existed.", ErrorCategory.NOT_FOUND, 404),
  RES002: spec("RES002", "Resource already exists", "Use a different identifier or update the existing resource instead.", ErrorCategory.CONFLICT, 409, { severity: "WARNING" }),
  // Rate limiting
  RATE001: spec("RATE001", "Rate limit exceeded", "Slow request rate and retry after the `Retry-After-Ms` interval.", ErrorCategory.RATE_LIMIT, 429, { retryable: true, severity: "WARNING" }),
  // External service
  EXT001: spec("EXT001", "External service unavailable", "An upstream provider is unavailable; retry the request, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 503, { retryable: true }),
  EXT002: spec("EXT002", "External service timeout", "An upstream provider timed out; retry, then contact support if persistent.", ErrorCategory.EXTERNAL_SERVICE, 504, { retryable: true }),
  // Database
  DB001: spec("DB001", "Database connection error", "Retry the request; if persistent, contact support.", ErrorCategory.DATABASE, 502, { retryable: true }),
  DB002: spec("DB002", "Database query error", "An internal query failed; retry and contact support if persistent.", ErrorCategory.DATABASE, 500),
  DB003: spec("DB003", "Database throttled", "Capacity throttled the request; retry after `Retry-After-Ms`.", ErrorCategory.DATABASE, 429, { retryable: true, severity: "WARNING" }),
  // Configuration / Internal
  CFG001: spec("CFG001", "Service configuration error", "An internal misconfiguration was detected; this is a server-side bug. Contact support.", ErrorCategory.CONFIGURATION, 500),
  INT001: spec("INT001", "Internal server error", "An unexpected error occurred. Retry, then contact support if persistent.", ErrorCategory.INTERNAL, 500),
  // Pipeline + data
  PIPE001: spec("PIPE001", "Pipeline processing error", "A pipeline step failed. The transaction will be retried automatically; contact support if it does not recover.", ErrorCategory.PIPELINE, 500, { retryable: true }),
  DATA001: spec("DATA001", "Data integrity error", "An expected invariant on the data was violated. This is a server-side bug; contact support.", ErrorCategory.DATA, 500)
};
function getCodeSpec(code) {
  for (const member of Object.values(ErrorCode)) {
    if (member.code === code) return member;
  }
  return null;
}
function errorCodeOf(error) {
  for (const candidate of [error.code, error.error_code]) {
    if (candidate && getCodeSpec(candidate)) return candidate;
  }
  return error.error_code ?? null;
}

// src/api/unwrap.ts
var ApiError = class extends Error {
  constructor(envelope) {
    super(envelope.error.message);
    this.envelope = envelope;
    this.name = "ApiError";
    this.statusCode = envelope.statusCode;
    this.code = errorCodeOf(envelope.error) ?? void 0;
    this.level = envelope.error.error_level;
    this.details = envelope.error.details;
    this.correlationId = envelope.error.correlation_id;
  }
  envelope;
  statusCode;
  code;
  level;
  details;
  correlationId;
};
function unwrapResponse(res) {
  if (res.ok) return res;
  throw new ApiError(res);
}
function unwrapData(res) {
  return unwrapResponse(res).data;
}

// src/plans/usageApi.ts
function asArray(raw) {
  return Array.isArray(raw) ? raw : [];
}
function asRecord(raw) {
  return raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
}
function asEpoch(raw) {
  const n = typeof raw === "string" ? Number(raw) : raw;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
}
function rollupFromWire(raw, events) {
  const wire = asRecord(raw);
  if (typeof wire.billable !== "number") return rollupUsage(events);
  return {
    billable: wire.billable,
    telemetry: typeof wire.telemetry === "number" ? wire.telemetry : 0,
    credits: typeof wire.credits === "number" ? wire.credits : 0,
    events: typeof wire.events === "number" ? wire.events : events.length,
    byKind: asRecord(wire.byKind)
  };
}
function meteringFromWire(raw) {
  const wire = asRecord(raw);
  if (typeof wire.available !== "boolean") return { available: true, reason: null };
  return {
    available: wire.available,
    reason: typeof wire.reason === "string" ? wire.reason : null,
    truncated: wire.truncated === true
  };
}
function periodFromWire(raw) {
  const wire = asRecord(raw);
  return {
    start: asEpoch(wire.start),
    end: asEpoch(wire.end),
    granularity: typeof wire.granularity === "string" ? wire.granularity : "custom"
  };
}
function groupsFromWire(raw) {
  if (!Array.isArray(raw)) return null;
  return raw.map((row) => {
    const g = asRecord(row);
    return {
      key: typeof g.key === "string" ? g.key : "unattributed",
      rollup: rollupFromWire(g.rollup, [])
    };
  });
}
function formatUsagePeriod(period) {
  if (!period) return "";
  if (period.granularity === "month" && period.start != null) {
    const start = new Date(period.start * 1e3);
    return `${start.toLocaleDateString(void 0, { month: "long", year: "numeric" })} to date`;
  }
  if (period.granularity === "month") return "This month to date";
  const fmt = (epoch) => epoch == null ? "\u2026" : new Date(epoch * 1e3).toLocaleDateString();
  if (period.start == null && period.end == null) return "All time";
  return `${fmt(period.start)} \u2014 ${fmt(period.end)}`;
}
function createUsageApi(client, options = {}) {
  const basePath = options.basePath ?? "/usage";
  return {
    async getUsage(query = {}) {
      const res = await client.get(basePath, {
        query: {
          start: query.start ?? null,
          end: query.end ?? null,
          pipeline_run_id: query.pipelineRunId ?? null,
          tx_id: query.txId ?? null,
          group_by: query.groupBy ?? null,
          customer_id: query.customerId ?? null,
          ...query.executions ? { executions: JSON.stringify(query.executions) } : {}
        }
      });
      const body = asRecord(unwrapData(res));
      const events = asArray(body.events);
      return {
        events,
        rollup: rollupFromWire(body.rollup, events),
        groups: groupsFromWire(body.groups),
        period: periodFromWire(body.period),
        attribution: asRecord(body.attribution),
        metering: meteringFromWire(body.metering)
      };
    }
  };
}
async function readExecutionUsage(api, executions) {
  const unique = [...new Map(executions.map((e) => [JSON.stringify(e), e])).values()];
  const reports = [];
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(2, Math.ceil(unique.length / 20)) }, async () => {
    while (next < unique.length) {
      const chunk = unique.slice(next, next += 20);
      const report = await api.getUsage({ executions: chunk });
      if (report.metering.available && !report.metering.reason && report.period.granularity !== "executions") {
        throw new Error("This deployment does not support execution-scoped usage yet");
      }
      reports.push(report);
    }
  }));
  const rollup = rollupUsage([]);
  for (const report of reports) {
    for (const key of ["billable", "telemetry", "credits", "events"]) {
      rollup[key] += report.rollup[key];
    }
    for (const [kind, count] of Object.entries(report.rollup.byKind)) {
      rollup.byKind[kind] = (rollup.byKind[kind] ?? 0) + count;
    }
  }
  const unavailable = reports.find((r) => !r.metering.available || r.metering.reason === "no_customer");
  return {
    events: reports.flatMap((r) => r.events).sort((a, b) => Date.parse(b.occurred_at) - Date.parse(a.occurred_at)).slice(0, 1e3),
    rollup,
    groups: null,
    period: { start: null, end: null, granularity: "executions" },
    attribution: reports[0]?.attribution ?? {},
    metering: {
      available: unavailable?.metering.available ?? true,
      reason: unavailable?.metering.reason ?? null,
      truncated: reports.some((r) => r.metering.truncated)
    }
  };
}

// src/plans/index.ts
var UsageEventKind = {
  PER_TX: "per_tx",
  /** One completed tracer run (an N-hop value-flow walk). Billable. */
  PER_TRACE: "per_trace",
  PER_ENRICHMENT: "per_enrichment",
  PER_STEP: "per_step",
  PER_PROVIDER_CALL: "per_provider_call",
  CREDIT: "credit"
};
var USAGE_UNIT_TERMS = {
  [UsageEventKind.PER_TX]: { billable: true, perTier: false },
  [UsageEventKind.PER_TRACE]: { billable: true, perTier: false },
  [UsageEventKind.PER_PROVIDER_CALL]: { billable: false, perTier: true },
  [UsageEventKind.PER_STEP]: { billable: false, perTier: true },
  [UsageEventKind.PER_ENRICHMENT]: { billable: false, perTier: true }
};
var BILLABLE_USAGE_KINDS = Object.keys(USAGE_UNIT_TERMS).filter((kind) => USAGE_UNIT_TERMS[kind].billable);
function isBillableKind(kind) {
  return kind !== UsageEventKind.CREDIT && USAGE_UNIT_TERMS[kind]?.billable === true;
}
var UsageUnit = {
  COUNT: "count",
  BYTES: "bytes",
  SECONDS: "seconds"
};
var UsageMetadataKey = {
  /** Whether this row should reach an invoice, decided once at record time. */
  BILLABLE: "billable",
  /** Ladder rung the unit's quota window counts at. */
  BILLING_SCOPE: "billing_scope",
  /** Which id format produced the row — `"v1"` before the cutover, `"v2"` after. */
  ATTRIBUTION_VERSION: "attribution_version"
};
function isCreditEvent(e) {
  return e.kind === UsageEventKind.CREDIT;
}
var EnforcementMode = { SOFT: "soft", HARD: "hard" };
var OveragePolicy = { ALLOW: "allow", THROTTLE: "throttle", REJECT: "reject" };
var QuotaStatus = { ALLOW: "allow", WARN: "warn", THROTTLE: "throttle", REJECT: "reject" };
function isBoundedWindow(w) {
  return w.granularity !== "all";
}
function policyMatches(policy, event) {
  if (event.kind !== policy.kind) return false;
  if (policy.subkind != null && event.subkind !== policy.subkind) return false;
  if (policy.applies_to_tiers && policy.applies_to_tiers.length > 0) {
    if (!policy.applies_to_tiers.includes(event.tier ?? "")) return false;
  }
  return true;
}
function isBlocking(d) {
  return d.status === QuotaStatus.REJECT || d.status === QuotaStatus.THROTTLE;
}
function isReject(d) {
  return d.status === QuotaStatus.REJECT;
}
function quotaUsedFraction(used, limit) {
  if (!limit || limit <= 0) return 0;
  return used / limit;
}
function evaluateQuota(policy, used) {
  const limit = policy.limit;
  const headroom = limit > 0 ? Math.max(0, limit - used) : Number.POSITIVE_INFINITY;
  const fraction = quotaUsedFraction(used, limit);
  let status = QuotaStatus.ALLOW;
  if (limit > 0 && used >= limit) {
    status = policy.enforcement === EnforcementMode.SOFT || policy.overage === OveragePolicy.ALLOW ? QuotaStatus.WARN : policy.overage === OveragePolicy.THROTTLE ? QuotaStatus.THROTTLE : QuotaStatus.REJECT;
  } else if (policy.soft_limit != null && used >= policy.soft_limit) {
    status = QuotaStatus.WARN;
  }
  return { status, headroom, fraction };
}
function hasEntitlement(plan, name) {
  return plan.entitlements.some((e) => e.name === name && e.enabled);
}
function getEntitlement(plan, name) {
  return plan.entitlements.find((e) => e.name === name) ?? null;
}
var ent = (name, enabled = true) => ({ name, enabled });
var win = (granularity) => ({ granularity });
var PLAN_FREE = {
  id: "free",
  display_name: "Free",
  description: "Trial / community tier with conservative monthly cap.",
  entitlements: [
    ent("catalog_lookup"),
    ent("address_catalog"),
    ent("tx_catalog"),
    ent("auto_tagging"),
    ent("risk_scoring", false),
    ent("price_enrichment", false),
    // Mirrors the shared pre-production plan default; overrides still apply.
    ent("triggers"),
    ent("webhook_callbacks", false),
    ent("graphql_fast_path"),
    ent("graphql_writes"),
    ent("graphql_per_operation"),
    ent("tenancy_switching"),
    ent("workspace_defaults"),
    ent("tenancy_creation"),
    ent("custom_roles"),
    ent("realtime_streaming"),
    ent("catalog_advanced"),
    ent("catalog_confidence_controls"),
    ent("actors"),
    ent("situations"),
    ent("custom_pipelines"),
    ent("custom_grouping_rules")
  ],
  quotas: [
    { id: "free_monthly_tx", kind: UsageEventKind.PER_TX, window: win("month"), limit: 1e4, soft_limit: 9e3, enforcement: EnforcementMode.HARD, overage: OveragePolicy.REJECT },
    { id: "free_minute_burst", kind: UsageEventKind.PER_TX, window: win("minute"), limit: 60, enforcement: EnforcementMode.HARD, overage: OveragePolicy.REJECT }
  ]
};
var PLAN_PRO = {
  id: "pro",
  display_name: "Pro",
  description: "Paid tier with higher caps and full enrichment surface.",
  entitlements: [
    ent("catalog_lookup"),
    ent("address_catalog"),
    ent("tx_catalog"),
    ent("auto_tagging"),
    ent("risk_scoring"),
    ent("price_enrichment"),
    ent("triggers"),
    ent("webhook_callbacks"),
    ent("graphql_fast_path"),
    ent("graphql_writes"),
    ent("graphql_per_operation"),
    ent("tenancy_switching"),
    ent("workspace_defaults"),
    ent("tenancy_creation"),
    ent("custom_roles"),
    ent("realtime_streaming"),
    ent("catalog_advanced"),
    ent("catalog_confidence_controls"),
    ent("actors"),
    ent("situations"),
    ent("custom_pipelines"),
    ent("custom_grouping_rules")
  ],
  quotas: [
    { id: "pro_monthly_tx", kind: UsageEventKind.PER_TX, window: win("month"), limit: 1e6, soft_limit: 9e5, enforcement: EnforcementMode.HARD, overage: OveragePolicy.THROTTLE },
    { id: "pro_minute_burst", kind: UsageEventKind.PER_TX, window: win("minute"), limit: 600, enforcement: EnforcementMode.HARD, overage: OveragePolicy.THROTTLE }
  ]
};
var PLAN_ENTERPRISE = {
  id: "enterprise",
  display_name: "Enterprise",
  description: "Custom-quota tier with soft enforcement (audit only).",
  entitlements: [
    ent("catalog_lookup"),
    ent("address_catalog"),
    ent("tx_catalog"),
    ent("auto_tagging"),
    ent("risk_scoring"),
    ent("price_enrichment"),
    ent("triggers"),
    ent("webhook_callbacks"),
    ent("graphql_fast_path"),
    ent("graphql_writes"),
    ent("graphql_per_operation"),
    ent("tenancy_switching"),
    ent("workspace_defaults"),
    ent("tenancy_creation"),
    ent("custom_roles"),
    ent("realtime_streaming"),
    ent("catalog_advanced"),
    ent("catalog_confidence_controls"),
    ent("actors"),
    ent("situations"),
    ent("custom_pipelines"),
    ent("custom_grouping_rules"),
    ent("sla_99_99")
  ],
  quotas: [
    { id: "enterprise_monthly_tx", kind: UsageEventKind.PER_TX, window: win("month"), limit: 0, enforcement: EnforcementMode.SOFT, overage: OveragePolicy.ALLOW }
  ]
};
function bundledPlans() {
  return [PLAN_FREE, PLAN_PRO, PLAN_ENTERPRISE];
}
var ENTITLEMENT = {
  /** Actor rankings and conduct, including their tenancy defaults. */
  ACTORS: "actors",
  SITUATIONS: "situations",
  TRIGGERS: "triggers",
  /** Reads may use the composed GraphQL path instead of REST. */
  GRAPHQL_FAST_PATH: "graphql_fast_path",
  /** Writes may go over GraphQL. */
  GRAPHQL_WRITES: "graphql_writes",
  /** The per-operation transport overrides are offered at all. */
  GRAPHQL_PER_OPERATION: "graphql_per_operation",
  /** Multi-tenant: the tenant switcher, and the tenancy admin surface. */
  TENANCY_SWITCHING: "tenancy_switching",
  /**
   * Creating ADDITIONAL organizations and tenants — a customer's first of each
   * comes with signup. Meant to be withheld on Free, where the create controls
   * are hidden rather than upsold; granted on every tier until packaging is
   * switched on (set `false` here AND in `plans.py`). The API refuses with
   * `PERM003` via `entitlement:tenancy_creation` on management-api's routes.
   */
  TENANCY_CREATION: "tenancy_creation",
  /**
   * Server real-time streaming: the AppSync socket that pushes transactions,
   * address watches, catalog, groups, notifications and collaboration. Without
   * it the dashboard opens no server connection and hides every live control;
   * cross-tab messaging inside the browser keeps working on every tier. The
   * AppSync subscribe / publishToChannel templates refuse it server-side.
   */
  REALTIME_STREAMING: "realtime_streaming",
  /**
   * Advanced catalog authoring: per-chain overrides and chain applicability,
   * primaries, entity membership, attaching to entities/events, team/user
   * sharing, verified status, bulk writes and review decisions. Without it the
   * advanced controls are hidden (basic annotation stays). Meant for Enterprise;
   * enforced server-side by commons.authz.catalog_authoring.
   */
  CATALOG_ADVANCED: "catalog_advanced",
  CATALOG_CONFIDENCE_CONTROLS: "catalog_confidence_controls",
  /** An admin may publish defaults that everyone in the tenant inherits. */
  WORKSPACE_DEFAULTS: "workspace_defaults",
  /**
   * Creating, editing and granting CUSTOM roles — team roles that are not
   * `system`, and org roles with a `rol_…` id. Built-in roles are every tier's.
   *
   * "Keep, but freeze": without it a customer keeps the custom roles they have
   * (and the people holding them keep them), may still delete one or take one
   * away, but cannot create, edit or hand one to anybody new. Enforced by
   * `commons.authz.custom_roles` (a `PERM003` refusal); the UI only withholds.
   *
   * Intended for Enterprise, granted on every tier today. Switching packaging
   * on is setting it `false` on free and pro here AND in `plans.py`.
   */
  CUSTOM_ROLES: "custom_roles",
  /**
   * Creating and editing grouping rules (`/grouping-rules`) — telling the engine
   * how to correlate transactions beyond its bundled recipes, which are every
   * tier's.
   *
   * "Keep, but freeze", as custom roles: without it a customer's existing rules
   * keep grouping and may still be disabled or deleted; creating one and
   * changing one stop. Enforced by `commons.authz.grouping_rules` (a `PERM003`
   * refusal); the UI only withholds.
   *
   * Meant for Enterprise, granted on every tier today. Switching packaging on is
   * setting it `false` on free and pro here AND in `plans.py`.
   */
  CUSTOM_GROUPING_RULES: "custom_grouping_rules"
};
var ENTITLEMENT_NAMES = new Set(
  bundledPlans().flatMap((plan) => plan.entitlements.map((e) => e.name))
);
function isKnownEntitlement(name) {
  return ENTITLEMENT_NAMES.has(name);
}
function overrideEnabled(raw) {
  if (typeof raw === "boolean") return raw;
  if (raw && typeof raw === "object" && "enabled" in raw) {
    const enabled = raw.enabled;
    if (typeof enabled === "boolean") return enabled;
  }
  return null;
}
function resolveEntitlements(plan, overrides = {}) {
  const resolved = plan.entitlements.map((e) => {
    const override = overrideEnabled(overrides[e.name]);
    return {
      name: e.name,
      enabled: override ?? e.enabled,
      overridden: override != null && override !== e.enabled
    };
  });
  for (const [name, raw] of Object.entries(overrides)) {
    if (plan.entitlements.some((e) => e.name === name)) continue;
    const override = overrideEnabled(raw);
    if (override == null) continue;
    resolved.push({ name, enabled: override, overridden: true });
  }
  return resolved;
}
var UNLIMITED = 0;
function overrideLimit(raw) {
  if (raw && typeof raw === "object" && "limit" in raw) {
    const value = raw.limit;
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}
function entitlementLimit(plan, overrides = {}, name) {
  if (!grantedEntitlements(plan, overrides).has(name)) return null;
  const override = overrideLimit(overrides[name]);
  const declared = plan.entitlements.find((e) => e.name === name)?.limit;
  const limit = override ?? declared ?? null;
  if (limit == null || !Number.isFinite(limit)) return null;
  return limit <= UNLIMITED ? null : limit;
}
function grantedEntitlements(plan, overrides = {}) {
  return new Set(
    resolveEntitlements(plan, overrides).filter((e) => e.enabled).map((e) => e.name)
  );
}
function resolvePlanById(planId) {
  return bundledPlans().find((p) => p.id === planId) ?? PLAN_FREE;
}
function isBillable(e) {
  const raw = e.metadata?.[UsageMetadataKey.BILLABLE];
  return raw === void 0 ? !isCreditEvent(e) : raw === true;
}
function signedQuantity(e) {
  return isCreditEvent(e) ? -e.quantity : e.quantity;
}
function rollupUsage(events) {
  const out = { billable: 0, telemetry: 0, byKind: {}, credits: 0, events: 0 };
  for (const e of events) {
    const signed = signedQuantity(e);
    out.events += 1;
    out.byKind[e.kind] = (out.byKind[e.kind] ?? 0) + signed;
    if (isCreditEvent(e)) out.credits += 1;
    if (isBillable(e)) out.billable += signed;
    else out.telemetry += signed;
  }
  return out;
}
function usageForRun(events, pipelineRunId) {
  return rollupUsage(events.filter((e) => e.pipeline_run_id === pipelineRunId));
}
function attributionPath(e) {
  const rungs = [
    ["customer", e.customer_id],
    ["org", e.org_id],
    ["tenant", e.tenant_id],
    ["user", e.user_id]
  ];
  return rungs.filter(([, id]) => Boolean(id) && id !== "main").map(([rung, id]) => ({ rung, id }));
}
function groupUsageBy(events, rung) {
  const read = (e) => {
    switch (rung) {
      case "customer":
        return e.customer_id;
      case "org":
        return e.org_id;
      case "tenant":
        return e.tenant_id;
      case "user":
        return e.user_id;
      case "actor":
        return e.actor;
    }
  };
  const buckets = /* @__PURE__ */ new Map();
  for (const e of events) {
    const bucket = read(e) ?? "unattributed";
    const existing = buckets.get(bucket);
    if (existing) existing.push(e);
    else buckets.set(bucket, [e]);
  }
  return [...buckets.entries()].map(([key, rows]) => ({ key, rollup: rollupUsage(rows) })).sort((a, b) => b.rollup.billable - a.rollup.billable);
}
function usedForPolicy(events, policy, period) {
  if (period.granularity !== policy.window.granularity) return null;
  let used = 0;
  for (const e of events) {
    if (policyMatches(policy, e)) used += signedQuantity(e);
  }
  return used;
}
function estimateRunCost(count, options = {}) {
  const kind = options.kind ?? UsageEventKind.PER_TX;
  const units = Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
  return {
    units: isBillableKind(kind) ? units : 0,
    kind,
    estimated: true
  };
}

export { BILLABLE_USAGE_KINDS, ENTITLEMENT, ENTITLEMENT_NAMES, EnforcementMode, OveragePolicy, PLAN_ENTERPRISE, PLAN_FREE, PLAN_PRO, QuotaStatus, UNLIMITED, USAGE_UNIT_TERMS, UsageEventKind, UsageMetadataKey, UsageUnit, attributionPath, bundledPlans, createUsageApi, entitlementLimit, estimateRunCost, evaluateQuota, formatUsagePeriod, getEntitlement, grantedEntitlements, groupUsageBy, hasEntitlement, isBillable, isBillableKind, isBlocking, isBoundedWindow, isCreditEvent, isKnownEntitlement, isReject, policyMatches, quotaUsedFraction, readExecutionUsage, resolveEntitlements, resolvePlanById, rollupUsage, signedQuantity, usageForRun, usedForPolicy };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map